import React, { MutableRefObject } from "react";
import * as CONSTANT from "../../utils/constant";
import { Input } from "semantic-ui-react";
import "../../css/Common.css";
import { useIntl } from "react-intl";

interface SearchBoxProps {
 defaultText?: string | number;
 defaultId?: string | number;
 defaultOptions?: any[];
 textField?: string;
 valueField?: string;
 onGetSearchOptions?: () => void;
 id?: string | number;
 disabled?: boolean;
 className?: string;
 icon?: string | Record<string,any>;
 minCharacters?: number;
 isShowAllOptions?: boolean;
 numberOfOptions?: number;
 onSelectOption?: () => void;
 inputClassName?: string;
 fetchOnFocus?: boolean;
 readOnly?: boolean;
 linkIcon?: boolean;
 onIconClick?: () => void;
 onClear?: () => void;
 contentStyle?: Record<string,any>;
 titleStyle?: Record<string,any>;
 inputStyle?: Record<string,any>;
 setClear?: boolean;
 testid?: string;
 placeholder?: string;
 isDataListBottom?: boolean;
 isError?: boolean;
 fluid?: boolean;
 onGetInputText?: () => void;
 toDisplay?: () => void;
}

const DEFAULT_PROPS = {
  defaultText: "",
  defaultId: null,
  defaultOptions: [],
  textField: "id",
  valueField: "id",
  id: "options", // if use this component several time in a component, must pass id; otherwise, datalist will not show up
  onGetSearchOptions: () => [null, null],
  disabled: false,
  className: "",
  minCharacters: 3,
  isShowAllOptions: false,
  numberOfOptions: 7,
  onSelectOption: () => {},
  icon: "search",
  inputClassName: "",
  fetchOnFocus: false,
  linkIcon: false,
  onIconClick: () => {},
  onClear: () => {},
  contentStyle: {},
  titleStyle: {},
  inputStyle: {},
  readOnly: false,
  setClear: false,
  placeholder: "",
  isDataListBottom: false,
  isError: false,
  fluid: false,
  autoComplete: "on",
  onGetInputText: () => {},
}
const SearchBox = React.forwardRef<any,SearchBoxProps>((propsParam, ref) => {
  const props = {
    ...DEFAULT_PROPS,
    ...Object.fromEntries(Object.entries(propsParam).filter(([_, value])=> value !== undefined))
  }
  const [inputText, setInputText] = React.useState("");
  const [inputId, setInputId] = React.useState(null);
  const [options, setOptions] = React.useState<any[]>([]);
  const [typingTimeout, setTypingTimeout] = React.useState(null);
  const [typingSearch, setTypingSearch] = React.useState(false);
  const [visible, setVisible] = React.useState(null);
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const [isLoading, setIsLoading] = React.useState(false);

  const inputRef = React.useRef();
  const itemRef = React.useRef();
  const searchBoxRef = React.useRef() as MutableRefObject<HTMLDivElement>;

  React.useEffect(() => {
    // console.log("props.defaultText", props.defaultText);
    setInputText(props.defaultText);
  }, [props.defaultText]);

  React.useEffect(() => {
    setInputId(props.defaultId);
  }, [props.defaultId]);

  React.useEffect(() => {
    setOptions(props.defaultOptions);
  }, [props.defaultOptions]);

  React.useEffect(() => {
    if (options && options.length > 0 && typingSearch) {
      setVisible(true as any);
    } else {
      setVisible(false as any);
      setTypingSearch(false);
    }
    if (inputId) {
      let index = options.findIndex((item: any) => item[props.valueField] === inputId);
      if (index > -1) {
        setInputText(options[index][props.textField]);
      }
    }
  }, [options]);

  React.useEffect(() => {
    if (props.setClear) {
      setInputText("");
      setInputId(null);
      props.onClear();
    }
  }, [props.setClear]);

  React.useImperativeHandle(ref, () => ({
    getText: () => {
      return inputText;
    },
    getId: () => {
      return inputId;
    },
    getItem: () => {
      let index = options.findIndex((item: any) => item[props.valueField] === inputId);
      if (index > -1) {
        return options[index];
      } else {
        return null;
      }
    },
    setText: (text: any) => {
      // @ts-ignore
      const elm = searchBoxRef.current.querySelector(".prompt.noPadding.noBorder").childNodes[0] as HTMLInputElement;
      if (elm) {
        elm.value = text;
      }
      setInputText(text);
    },
    setTextByElement: (text: any) => {
      // @ts-ignore
      const elm = searchBoxRef.current.querySelector(".prompt.noPadding.noBorder").childNodes[0] as HTMLInputElement;
      if (elm) {
        elm.value = text;
      }
      setInputText(text);
    },
    setId: (id: any) => {
      setInputId(id);
    },
    setOptions: (options: any) => {
      setOptions(options);
    },
    handleSearch: (searchQuery: any) => {
      console.log(isLoading);
      setTypingTimeout(setTimeout(() => getDataList({ searchQuery }), 400) as any);
    },
    clear: () => {
      setInputText("");
      setInputId(null);
      setOptions([]);
    },
  }));

  React.useEffect(() => {
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  });

  function handleClickOutside(event: any) {
    if (
      inputRef.current &&
      // @ts-ignore
      inputRef.current.inputRef &&
      // @ts-ignore
      inputRef.current.inputRef.current &&
      // @ts-ignore
      !inputRef.current.inputRef.current.contains(event.target) &&
      // @ts-ignore
      itemRef.current &&
      !(itemRef.current as any).contains(event.target)
    ) {
      setVisible(false as any);
      setTypingSearch(false);
    }
  }

  const handleSearch = async (e: any) => {
    setTypingSearch(true);
    const searchQuery = e.target.value;
    let searchIndex = options.findIndex((items) => items[props.textField] === searchQuery);
    if (searchIndex > -1) {
      setInputText(options[searchIndex][props.textField]);
      setInputId(options[searchIndex][props.valueField]);
      return;
    }
    setInputText(searchQuery);
    setInputId(null);
    if (typingTimeout) {
      clearTimeout(typingTimeout as any);
    }
    setTypingTimeout(setTimeout(() => getDataList({ searchQuery }), 400) as any);

    props.onGetInputText?.(searchQuery);
  };

  const getDataList = async ({ searchQuery }: any = {}) => {
    if (searchQuery.length === 0) {
      props.onClear();
    }
    if (searchQuery.length >= props.minCharacters || props.fetchOnFocus) {
      setIsLoading(true);
      const [optionsData, optionsError] = await props.onGetSearchOptions({
        searchText: searchQuery,
      });
      if (optionsData) {
        let data = optionsData;
        if (optionsData.items) {
          data = optionsData.items;
        }
        let shortOptions = data?.slice(0, props.numberOfOptions);

        if (props.isShowAllOptions) {
          setOptions(data);
        } else {
          setOptions(shortOptions);
        }
      }
      setIsLoading(false);
    }
  };

  const handleSelectItem = (e: any, index: any) => {
    if (options[index]) {
      setInputId(options[index][props.valueField]);
      setInputText(options[index][props.textField]);
      props.onSelectOption({ item: options[index], id: options[index][props.valueField] });
    }
    setVisible(false as any);
    setTypingSearch(false);
  };

  const generateDataList = (id?: string) => {
    let data: any[] = [];
    for (let items in options) {
      const text = options[items][props.textField];

      data.push(
        <a
          id={`${id}-Item-${items}`}
          key={`searchItem${options[items][props.valueField]}`}
          onMouseOver={() => setActiveIndex(parseInt(items))}
          className={"result " + (activeIndex === parseInt(items) ? "active" : "")}
          onClick={(e) => handleSelectItem(e, items)}
        >
          <div className="content" style={{ ...props.contentStyle }}>
            <div className="title" style={{ ...props.titleStyle }}>
              {props.toDisplay ? props.toDisplay(text, options[items]) : text}
            </div>
          </div>
        </a>
      );
    }
    return data;
  };

  const handleKeyDown = (e: any) => {
    // ESC
    if (e.keyCode === CONSTANT.KEY_CODE.ESCAPE) {
      setVisible(false as any);
      setTypingSearch(false);
    } else if (e.keyCode === CONSTANT.KEY_CODE.ARROW_DOWN && activeIndex !== options.length - 1) {
      setActiveIndex(activeIndex + 1);
    } else if (e.keyCode === CONSTANT.KEY_CODE.ARROW_UP && activeIndex !== 0) {
      setActiveIndex(activeIndex - 1);
    } else if (e.keyCode === CONSTANT.KEY_CODE.ENTER) {
      handleSelectItem(e, activeIndex);
    }
  };

  let icon = {
    name: props.icon,
    link: props.linkIcon,
    onClick: props.onIconClick,
  };
  if (typeof props.icon === "object") {
    icon = props.icon;
  }

  return (
    <div ref={searchBoxRef} className="ui fluid search">
      {" "}
      {/* fluid make the dropdown fluid */}
      <Input
        id={props.id}
        readOnly={props.readOnly}
        disabled={props.disabled}
        className={"prompt noPadding noBorder " + (isLoading ? "loading" : "") + props.inputClassName}
        style={{ ...props.inputStyle }}
        onKeyDown={handleKeyDown}
        onChange={handleSearch}
        value={inputText || ""}
        ref={inputRef as any}
        icon={icon} // send icon={null} if do not want to show icon
        onFocus={() => {
          if (props.fetchOnFocus) {
            setTypingSearch(true);
            getDataList({ searchQuery: inputText });
          }
        }}
        placeholder={props.placeholder}
        error={props.isError}
        fluid={props.fluid}
        autoComplete={props.autoComplete}
      />
      <div
        ref={itemRef as any}
        className={"results transition " + (visible ? "visible" : "hidden")} // results make dropdown on top, transition show the dropdown
        style={{
          bottom: props.isDataListBottom && "120%",
          top: props.isDataListBottom && "auto",
        }}
      >
        {generateDataList(props.id)}
      </div>
    </div>
  );
});

SearchBox.displayName = "SearchBox";
export default React.memo(SearchBox);
